import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import Icon from "@ui-kit/icons/Icon.web";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { FacebookOAuthService } from "@shared/modules/auth/services/FacebookOAuthService";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import { useQueryClient } from "react-query";
import { AnalyticsService } from "@shared/services/analytics";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import SocialLoginButton from "../SocialLoginButton/SocialLoginButton.web";
import type { FacebookLoginButtonSharedProps } from "./FacebookLoginButton.props";

const authProfileRepo = new AuthProfileRepository();

export default function FacebookLoginButton(
  props: FacebookLoginButtonProps
): JSX.Element {
  const { title, onSuccess, className = "" } = props;
  const notifier = NotifierFactory.createNotifier();
  const { t } = useTranslation("common");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();
  const broadcastChannel = useRef(
    new BroadcastChannel(FacebookOAuthService.AUTH_CHANNEL)
  );
  const queryClient = useQueryClient();
  const { setLoggedInStates } = useUserSessionAuthState();

  useEffect(() => {
    // Store the current broadcast channel reference in a variable
    const bc = broadcastChannel.current;

    // Event handler for receiving messages from the broadcast channel
    const handleMessage = (event: MessageEvent) => {
      if (event.data === FacebookOAuthService.MESSAGES.USER_AUTHENTICATED) {
        // Use a button click as we lack custom interaction types
        AnalyticsService.buttonClick({
          custom_params: {
            datum: "authenticated",
          },
          label: "facebook_login_button",
        });

        // Normally handled by useUserSessionAuthState but oAuth logins don't work with it
        queryClient.invalidateQueries(authProfileRepo.repositoryName);
        setLoggedInStates?.();
        onSuccess();
        setIsLoading(false);
      }
    };

    // Add event listener for messages from the broadcast channel
    bc.addEventListener("message", handleMessage);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      bc.removeEventListener("message", handleMessage);
    };
  }, [onSuccess]);

  const openFacebookLogin = useCallback(() => {
    AnalyticsService.buttonClick({
      label: "facebook_login_button",
      custom_params: {
        datum: "open",
      },
    });

    // Build the authentication URL with optional nextUrl parameter
    const nextUrl = router.query.next ? `${router.query.next}` : undefined;
    const authUrl = FacebookOAuthService.buildAuthUrl({ nextUrl });

    // Calculate the position for the popup window
    const width = 600;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Open a new popup window for Facebook login
    const authWindow = window.open(
      authUrl,
      "AuthWindow",
      `width=${width}, height=${height}, top=${top}, left=${left}`
    );

    if (!authWindow) {
      setIsLoading(false);
      notifier.error(
        t(
          "Failed to open the authentication window. Please disable your popup blocker and try again."
        )
      );
      return;
    }

    setIsLoading(true);

    const userAuthenticated = false;

    // Interval to check if the popup window is closed
    const checkClosed = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkClosed);
        if (!userAuthenticated) {
          setIsLoading(false);
        }
      }
    }, 500);
  }, [router.query.next, onSuccess]);

  return (
    <SocialLoginButton
      title={title}
      icon={<Icon.FacebookLogo boxSize="element.lg" />}
      onClick={openFacebookLogin}
      className={className}
      isLoading={isLoading}
    />
  );
}

export type FacebookLoginButtonProps = FacebookLoginButtonSharedProps & {
  // Add any web specific props here.
};
